import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  bulkUploadScraps,
  getScrapBulkUploadHistory,
  downloadScrapErrorReport,
} from "../../../redux/action/scraps";
import { getOrganization } from "../../../redux/action/organization";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/templates/scrap-bulk-upload-template.xlsx`;

const statusPill = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "partially_completed":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "muted";
  }
};

const statusLabel = (status) =>
  ({
    completed: "Completed",
    partially_completed: "Partially completed",
    failed: "Failed",
    processing: "Processing",
  }[status] || status || "-");

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return value;
  }
};

function BulkUploadScraps() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bulkUploading, bulkHistory, bulkHistoryLoading } = useSelector(
    (state) => state.scraps
  );

  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    dispatch(getScrapBulkUploadHistory());
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    dispatch(
      bulkUploadScraps(file, (data) => {
        setResult(data);
        setFile(null);

        // Created scraps are linked to the organization server-side; refresh
        // the org so the Scraps table reflects them.
        dispatch(getOrganization());
        dispatch(getScrapBulkUploadHistory());
      })
    );
  };

  return (
    <div className="flex column gap-1 p-1">
      <div className="flex align-center gap-1">
        <span
          className="material-symbols-outlined"
          style={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
        >
          arrow_back
        </span>
        <h5 className="bold" style={{ margin: 0 }}>
          Bulk Upload Scraps
        </h5>
      </div>

      {/* Upload card */}
      <div className="rounded white p-1">
        <ol style={{ paddingLeft: 18, marginBottom: 16, lineHeight: 1.7 }}>
          <li>
            Download the template and fill in your scraps. Keep the column
            headers unchanged.
          </li>
          <li>
            Use the <b>Allowed Values</b> sheet for valid <b>UOM</b>,{" "}
            <b>Type</b>, <b>Classification</b> and <b>Disposal Route</b> options.
          </li>
          <li>Upload the filled file below and click Submit.</li>
        </ol>

        <a
          href={TEMPLATE_URL}
          download="scrap-bulk-upload-template.xlsx"
          className="btn"
          style={{
            background: "#fff",
            color: "var(--primary-color)",
            border: "1px solid var(--primary-color)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 16,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            download
          </span>
          Download Template
        </a>

        <form
          onSubmit={handleSubmit}
          className="flex column gap-1"
          style={{ maxWidth: 520 }}
        >
          <div className="flex-column full-width">
            <label>Upload filled template (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="browser-default input-style"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setResult(null);
              }}
            />
          </div>

          <button
            type="submit"
            className="btn"
            style={{
              background: "var(--primary-color)",
              border: "none",
              maxWidth: 200,
            }}
            disabled={!file || bulkUploading}
          >
            {bulkUploading ? "Uploading..." : "Submit"}
          </button>
        </form>

        {result && (
          <div
            className="rounded p-1 mt-1"
            style={{
              background: "#f5f7fa",
              border: "1px solid #e0e0e0",
              maxWidth: 640,
            }}
          >
            <div className="bold mb-1">{result.message}</div>
            <div
              className="flex gap-1"
              style={{ flexWrap: "wrap", alignItems: "center" }}
            >
              <span className="trader-pill">Total: {result.totalRows}</span>
              <span className="trader-pill success">
                Added: {result.successCount}
              </span>
              <span
                className={`trader-pill ${
                  result.failedCount ? "danger" : "muted"
                }`}
              >
                Failed: {result.failedCount}
              </span>
              {result.failedCount > 0 && result.historyId && (
                <button
                  type="button"
                  className="btn"
                  style={{
                    background: "#fff",
                    color: "var(--primary-color)",
                    border: "1px solid var(--primary-color)",
                  }}
                  onClick={() =>
                    dispatch(
                      downloadScrapErrorReport(result.historyId, "latest-upload")
                    )
                  }
                >
                  Download error report
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload history */}
      <div className="flex space-between align-center mt-1">
        <h6 className="bold" style={{ margin: 0 }}>
          Upload History
        </h6>
        <span
          className="material-symbols-outlined"
          style={{ cursor: "pointer", color: "var(--primary-color)" }}
          title="Refresh"
          onClick={() => dispatch(getScrapBulkUploadHistory())}
        >
          refresh
        </span>
      </div>

      <div className="rounded white">
        <table className="striped responsive-table dashboardtablev2">
          <thead>
            <tr className="table-header-row">
              <th>File</th>
              <th>Uploaded On</th>
              <th>Total</th>
              <th>Completed</th>
              <th>Failed</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {bulkHistoryLoading && (
              <tr>
                <td colSpan={7}>Loading...</td>
              </tr>
            )}

            {!bulkHistoryLoading && (bulkHistory || []).length === 0 && (
              <tr>
                <td colSpan={7}>No bulk uploads yet</td>
              </tr>
            )}

            {!bulkHistoryLoading &&
              (bulkHistory || []).map((entry) => (
                <tr key={entry._id}>
                  <td>{entry.fileName || "-"}</td>
                  <td>{formatDate(entry.createdAt)}</td>
                  <td>{entry.totalScraps ?? 0}</td>
                  <td>{entry.completedCount ?? 0}</td>
                  <td>{entry.failedCount ?? 0}</td>
                  <td>
                    <span className={`trader-pill ${statusPill(entry.status)}`}>
                      {statusLabel(entry.status)}
                    </span>
                  </td>
                  <td>
                    {entry.failedCount > 0 ? (
                      <button
                        type="button"
                        className="btn-flat"
                        style={{
                          color: "var(--primary-color)",
                          textTransform: "none",
                          padding: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        onClick={() =>
                          dispatch(
                            downloadScrapErrorReport(entry._id, entry.fileName)
                          )
                        }
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: 18 }}
                        >
                          download
                        </span>
                        Error report
                      </button>
                    ) : (
                      <span style={{ color: "#9e9e9e" }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BulkUploadScraps;
